import { Injectable } from '@angular/core';
import { RmmCustomAlertingServiceController } from '@modules/rmm-custom-alerting-module/services/rmm-custom-alerting-controller.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { ModalService } from 'mbs-ui-kit';
import { NotificationPlanRule, NotifyToPlatform } from 'mbs-ui-shared';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { GetComputersParams } from 'mbs-ui-shared@models/Computer';
import {
  CustomAlertingSettings,
  NotificationProfileTypes,
  NotificationProfileTypesInNumber,
  ProfilePostActions,
  RMMAlertTypes,
  RmmNotificationProfile,
  RmmProfileApplyTo
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import moment from 'moment';
import { BehaviorSubject, from } from 'rxjs';
import { RMMCustomNotificationWizardComponent } from '../rmm-custom-notification-wizard.component';

export type RmmAlertWizardDataIncome = {
  filter: GetComputersParams;
  hids: string[];
  totalCount: number;
  windowsAlertsEnabled: boolean;
  profileMode?: boolean;
  showConnectWise?: boolean;
  profileData: {
    applyTo?: RmmProfileApplyTo;
    editMode?: boolean;
    profileId?: string;
    profileType?: NotificationProfileTypesInNumber;
    profileName?: string;
    notifications?: CustomAlertingSettings;
    profileActionPayload?: ProfilePostActions;
    created?: string;
  };
};
@UntilDestroy()
@Injectable()
export class LoadRmmCustomNotificationWizardService {
  public hids: string[];
  public filter: GetComputersParams;
  public totalCount: number;
  public windowsAlertsEnabled: boolean;

  public providerEmail: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public pushAdminNameList: string;
  public recipientsPayload: BehaviorSubject<Partial<CustomAlertingSettings>> = new BehaviorSubject(null);
  public onPostActionsReLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public profileActionPayload: BehaviorSubject<ProfilePostActions> = new BehaviorSubject(null);
  public notificationsToSave: BehaviorSubject<Partial<CustomAlertingSettings>> = new BehaviorSubject(null);
  public settingsData: BehaviorSubject<Partial<CustomAlertingSettings>> = new BehaviorSubject(null);
  public applyTo: BehaviorSubject<RmmProfileApplyTo> = new BehaviorSubject({
    allCompanies: false,
    companies: [],
    computers: [],
    computerTags: []
  });
  public profileName: BehaviorSubject<string> = new BehaviorSubject(null);
  public showApplyToStep = false;
  public canEditApplyToStep = true;
  public editMode = false;
  public profileId: string = null;
  public created: string;
  public showConnectWise = false;
  public profileType: NotificationProfileTypesInNumber = null;

  private readonly DefaultRecipient = {
    recipient: {
      type: NotificationPlanRule.None,
      emails: []
    },
    pushNotification: {
      enabled: false
    },
    pushRecipient: {
      type: NotificationPlanRule.None,
      users: []
    }
  };
  public readonly DefaultActionPayload: ProfilePostActions = {
    general: {
      recipient: {
        type: NotificationPlanRule.None,
        emails: []
      },
      pushNotification: {
        enabled: false
      },
      pushRecipient: {
        type: NotificationPlanRule.None,
        users: []
      },
      connectWise: { enabled: false },
      windowsScriptGuid: null,
      linuxScriptGuid: null,
      macOsScriptGuid: null
    }
  };

  constructor(
    private i18next: I18NextService,
    private modalService: ModalService,
    private auth: AuthFacade,
    private customAlertService: RmmCustomAlertingServiceController
  ) {
    this.auth.currentUser$
      .pipe(untilDestroyed(this))
      .subscribe((user) => this.providerEmail.next(user?.ProviderInfo?.EmailNotificationsTo));
  }

  loadWizardStream(data: RmmAlertWizardDataIncome = null) {
    this.hids = data?.hids ?? [];
    this.filter = data?.filter ?? null;
    this.totalCount = data?.totalCount ?? 0;
    this.windowsAlertsEnabled = data?.windowsAlertsEnabled;
    this.showApplyToStep = !!data?.profileMode;
    this.editMode = !!data?.profileData?.editMode;
    this.profileId = data?.profileData?.profileId;
    this.profileType = data?.profileData?.profileType;
    this.showConnectWise = !!data?.showConnectWise;
    this.created = data?.profileData?.created ?? moment(new Date()).toString();

    this.profileActionPayload.next(
      this.canUsePerAlertPostActions(data?.profileData?.notifications) && data?.profileData?.profileActionPayload
        ? data?.profileData?.profileActionPayload
        : this.generatePostActionsFromNotifications(data?.profileData?.notifications)
    );

    if (data?.profileData?.profileType) this.canEditApplyToStep = !data?.profileData?.profileId;

    if (data?.profileData?.profileName) this.profileName.next(data?.profileData?.profileName);
    if (data?.profileData?.applyTo) this.applyTo.next({ ...data?.profileData?.applyTo });

    this.onPostActionsReLoaded.next(true);

    return from(this.modalService.openWizard(RMMCustomNotificationWizardComponent));
  }

  canUsePerAlertPostActions(notification: CustomAlertingSettings) {
    return Object.keys(RMMAlertTypes).some(
      (key) => notification && RMMAlertTypes[key] in notification && notification[RMMAlertTypes[key]]?.enabled
    );
  }

  generatePostActionsFromNotifications(notifications: CustomAlertingSettings) {
    return {
      general: {
        recipient: {
          type: notifications?.recipient?.type ?? this.DefaultActionPayload.general.recipient.type,
          emails: notifications?.recipient?.emails ?? this.DefaultActionPayload.general.recipient.emails
        },
        pushNotification: {
          enabled: notifications?.pushNotification?.enabled ?? this.DefaultActionPayload.general.pushNotification.enabled
        },
        pushRecipient: {
          type: notifications?.pushRecipient?.type ?? this.DefaultActionPayload.general.pushRecipient.type,
          users: notifications?.pushRecipient?.users ?? this.DefaultActionPayload.general.pushRecipient.users
        },
        connectWise: { enabled: false },
        windowsScriptGuid: null,
        linuxScriptGuid: null,
        macOsScriptGuid: null
      }
    };
  }

  resetWizardData() {
    this.hids = [];
    this.filter = null;
    this.totalCount = 0;
    this.windowsAlertsEnabled = true;
    this.canEditApplyToStep = true;
    this.showApplyToStep = false;
    this.editMode = false;
    this.profileId = null;

    this.profileActionPayload.next(this.DefaultActionPayload);
    this.profileName.next(null);
    this.applyTo.next({
      allCompanies: false,
      companies: [],
      computers: [],
      computerTags: []
    });
  }

  // Payload to save
  getFullPayload(isCustom = false) {
    const payload: any = {
      applyTo: {
        allCompanies: this.showApplyToStep ? this.applyTo.getValue()?.allCompanies : false,
        companies: this.showApplyToStep ? this.applyTo.getValue()?.companies : [],
        computerTags: this.showApplyToStep ? this.applyTo.getValue()?.computerTags : [],
        computers: this.showApplyToStep
          ? this.applyTo.getValue()?.computers
          : this.hids.map((hid) => ({ hid, name: null, disabled: false }))
      },
      notifications: {
        ...(isCustom ? this.getPayloadFromNotifications() : this.settingsData.getValue()),
        ...this.recipientsPayload.getValue()
      }
    };

    if (!this.showApplyToStep) {
      if (this.filter) payload.applyTo.contextSearch = JSON.stringify(this.filter);
    } else {
      payload.notifications = {
        ...payload.notifications,
        ...this.DefaultRecipient
      };
      payload.name = this.profileName.getValue();
      payload.postActions = this.profileActionPayload.getValue();
    }

    return payload;
  }

  getProfileType(payload: Partial<RmmNotificationProfile>): NotificationProfileTypes {
    const applyTo = payload.applyTo;

    if (applyTo.allCompanies && !applyTo.computerTags.length && !applyTo.computers.length) return NotificationProfileTypes.Global;

    if (applyTo.companies.length && !applyTo.computerTags.length && !applyTo.computers.length) return NotificationProfileTypes.Company;

    if (!applyTo.allCompanies && !applyTo.companies.length && !applyTo.computerTags.length && applyTo.computers.length)
      return NotificationProfileTypes.Computer;

    return NotificationProfileTypes.Custom;
  }

  getPayloadFromNotifications() {
    const payload = {};
    Object.keys(RMMAlertTypes).forEach((key) => Object.assign(payload, { [RMMAlertTypes[key]]: this.getObjectValueFromForm(key) }));
    return this.customAlertService.getConnectionLossSettings(payload);
  }

  private getObjectValueFromForm(key: string) {
    const formData = { ...this.notificationsToSave.getValue()[RMMAlertTypes[key]] };

    switch (RMMAlertTypes[key]) {
      case RMMAlertTypes.HighCpuUsage:
      case RMMAlertTypes.HighMemoryUsage:
        return {
          alertPercent: Number(formData['alertPercent']),
          duration: formData['duration'],
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.LowDiskSpace:
        return {
          alertPercent: Number(formData['alertPercent']),
          duration: formData['duration'],
          enabled: !!formData['enabled'],
          excludedVolumeLabels: this.getFormArrayValueFromString(formData['excludedVolumeLabels']),
          excludedMountPoints: this.getFormArrayValueFromString(formData['excludedMountPoints']),
          excludeBootVolume: formData['excludeBootVolume'],
          excludeRemovableDisks: formData['excludeRemovableDisks']
        };
      case RMMAlertTypes.SmartNotHealthy:
      case RMMAlertTypes.NoAntivirus:
      case RMMAlertTypes.NotUpdated:
        return {
          duration: formData['duration'],
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.SoftwareChanges:
        return {
          excludedNames: this.getFormArrayValueFromString(formData['excludedNames']),
          excludedVendor: this.getFormArrayValueFromString(formData['excludedVendor']),
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.HardwareChanges:
        return {
          excludedNames: this.getFormArrayValueFromString(formData['excludedNames']),
          excludedPNPClasses: this.getFormArrayValueFromString(formData['excludedPNPClasses']),
          excludedPNPDeviceIDs: this.getFormArrayValueFromString(formData['excludedPNPDeviceIDs']),
          excludedManufacturer: this.getFormArrayValueFromString(formData['excludedManufacturer']),
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.ConnectionLoss:
        return {
          enabled: !!formData['enabled'],
          notifyToPlatform: formData['enabled'] ? formData['notifyToPlatform'] : NotifyToPlatform.All,
          notifyOnRestore: formData['enabled'] ? formData['notifyOnRestore'] : false,
          duration: formData['duration']
        };
      case RMMAlertTypes.SnmpDevice:
        return {
          alerts: formData['alerts'] ?? [],
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.HighTemperature:
        return {
          alertHighTemperature: Number(formData['alertHighTemperature']),
          duration: formData['duration'],
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.WrongSystemTime:
        return {
          alertWrongSystemTimeDifference: formData['alertWrongSystemTimeDifference'],
          enabled: !!formData['enabled']
        };
      case RMMAlertTypes.ServiceState:
        return {
          includeServices: formData['includeServices'] ?? [],
          enabled: !!formData['enabled']
        };
      default:
        return {};
    }
  }

  getFormArrayValueFromString(values: any) {
    return this.customAlertService.getFormArrayValueFromString(values);
  }
}
